import { capitalize, get } from 'lodash';

/**
 * Contains helper functions for working with Amplitude Sagas
 */
export default {
    /**
     * Transforms submission data into an Amplitude submission event
     * Depending on data, server may succesfully save submission or not
     * In any case we try to take as much information as possible for transforming to Amplitude Event Data
     *
     * @param {Object} submission The submission to transform
     * @param {String} status Status of this submission
     * @param {Object} payload Submision's payload sent to the server
     *
     * @returns {object}
     */
    transformSubmissionToEventData(submission, status, payload) {
        if (submission && submission.ratings) {
            return this.transformSubmissionRatingsToEventData(submission, status);
        }

        if (submission && payload) {
            return this.transformSubmissionPayloadToEventData(payload, status, submission);
        }

        return {};
    },

    /**
     * Transforms data from submisison ratings
     *
     * @param {Object} submission The submission to transform
     * @param {String} status Status of this submission
     *
     * @returns {object}
     */
    transformSubmissionRatingsToEventData(submission, status) {
        const rating = submission.ratings[submission.type];
        const {
            identityRecoveryCoverage,
            identityRecoveryPremium,
            anticipatedProjectLength,
            excessPremium,
            excessTerrorPremium,
            instantQuote,
            premium,
            terrorPremium,
            totalExcessPremium,
            totalPremium
        } = rating;

        let additionalCoverages = [];
        let optionalCoverages = [];

        const additionalCoverage = get(submission, 'coverages.gl.additional', {});
        const optionalCoverage = get(submission, 'coverages.gl.optional', {});

        for (const [key, value] of Object.entries(additionalCoverage)) {
            if (value.selected === true) additionalCoverages.push(key);
        }

        for (const [key, value] of Object.entries(optionalCoverage)) {
            if (value.selected === true) optionalCoverages.push(key);
        }

        return {
            BaseCoverage: get(submission, 'coverages.gl.base'),
            AdditionalCoverages: additionalCoverages.join(','),
            OptionalCoverages: optionalCoverages.join(','),
            ClearanceStatus: submission.clearanceStatus
                ? capitalize(submission.clearanceStatus)
                : '',
            NamedInsured: submission.primaryInsuredName,
            Outcome: instantQuote ? 'Quoted' : 'Referral',
            PremiumExcessBase: excessPremium,
            PremiumExcessTerror: excessTerrorPremium,
            PremiumExcessTotal: totalExcessPremium,
            PremiumGLIRC: identityRecoveryCoverage,
            PremiumGLBase: premium,
            PremiumGLTerror: terrorPremium,
            PremiumGLTotal: totalPremium,
            Product: submission.type ? submission.type.toUpperCase() : '',
            ProjectAddressState: submission.projectAddress
                ? submission.projectAddress.projectState
                : '',
            ReferralReasons: rating.reason ? rating.reason : [],
            SubmissionId: submission.id,
            SubmissionStatus: status
        };
    },

    /**
     * Transforms data from submission's API payload
     *
     * @param {*} payload Submision's payload sent to the server
     * @param {*} status Status of this submission
     *
     * @returns {object}
     */
    transformSubmissionPayloadToEventData(payload, status, response) {
        let additionalCoverages = [];
        let optionalCoverages = [];
        const additionalCoverage = get(payload, 'coverages.gl.additional', {});
        const optionalCoverage = get(payload, 'coverages.gl.optional', {});
        for (const [key, value] of Object.entries(additionalCoverage)) {
            if (value.selected === true) additionalCoverages.push(key);
        }
        for (const [key, value] of Object.entries(optionalCoverage)) {
            if (value.selected === true) optionalCoverages.push(key);
        }
        var reason = "";
        if (response && response[0] && response[0].reason) {
            reason = response[0].reason;
        } else if (typeof response.result === "string") {
            reason = response.result;
        } else if (typeof response === "string") {
            reason = response;
        }

        return {
            BaseCoverage: get(payload, 'coverages.gl.base'),
            AdditionalCoverages: additionalCoverages.join(','),
            OptionalCoverages: optionalCoverages.join(','),
            ClearanceStatus: payload.clearanceStatus ? capitalize(payload.clearanceStatus) : '',
            NamedInsured: payload.primaryInsuredName,
            Outcome: reason == 'submission_failed_clearance' ? 'Unknown' : 'Referral',
            PremiumExcessBase: 0,
            PremiumExcessTerror: 0,
            PremiumExcessTotal: 0,
            PremiumGLIRC: 0,
            PremiumGLBase: 0,
            PremiumGLTerror: 0,
            PremiumGLTotal: 0,
            Product: payload.type ? payload.type.toUpperCase() : '',
            ProjectAddressState: payload.projectAddress ? payload.projectAddress.projectState : '',
            ReferralReasons: reason,
            SubmissionId: payload.id,
            SubmissionStatus: status
        };
    },

    /**
     * Amplitude only supports primitive types as nested items in arrays.
     *
     * As a workaround, this function converts the matches to an object with the index
     * of the match being the key:
     *
     * ex: [{name: '', address: '', ...}] => { '0': {name: '', address: ''}, '1': {...}}
     * @param {*} matches
     * @returns {array} array of transformed matches
     */
    transformMatches(matches) {
        return matches.reduce((prev, curr, idx) => {
            prev[idx.toString()] = curr;
            return prev;
        }, {});
    }
};
